import { useEffect, useState, type FormEvent } from "react";
import { markeerTaalGekozen } from "../TaalVanAccount";
import { useLogin, useTranslate, useSetLocale, useGetLocale } from "@refinedev/core";
import { Field, PasswordField, ToggleField, Button, UserIcon, LockIcon } from "@fawkes/ui";

import { fetchAuthConfig } from "../authProvider";
import "./LoginPage.css";

// Provisioning (HelloID) login is feature-gated, mirroring the Jmix LoginView's
// `provisioningProperties.isLoginEnabled()` — hidden unless the env flag is set.
const PROVISIONING_LOGIN_ENABLED =
  (import.meta.env.VITE_PROVISIONING_LOGIN as string | undefined) === "true";

const LOCALES = [
  { code: "nl", flag: "🇳🇱", label: "Nederlands" },
  { code: "en", flag: "🇬🇧", label: "English" },
] as const;

// jve-login: the SPA login page — a token-built, @fawkes/ui recreation of the legacy Jmix LoginView
// (src/.../view/login). The centred card on the shelter background, lock-badged "Docasoft" header,
// icon-prefixed username/password fields, remember-me, locale switch and the green submit button all
// mirror login-view.css. The submit wiring (OIDC redirect vs. credential endpoint) lands in step 2.
export function LoginPage() {
  const t = useTranslate();
  const setLocale = useSetLocale();
  const getLocale = useGetLocale();
  const locale = getLocale() ?? "nl";

  const { mutate: login, isLoading } = useLogin();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const [error, setError] = useState(false);
  // "Remember me" is only offered when the server enables it (fawkes.api.login.remember-enabled,
  // on by default). The checkbox is coupled to that server flag: when the option is on it starts
  // ticked (persistent session by default), and the operator can untick it for a session-only cookie.
  const [rememberOffered, setRememberOffered] = useState(false);

  useEffect(() => {
    let active = true;
    void fetchAuthConfig().then((cfg) => {
      if (!active) return;
      setRememberOffered(cfg.rememberEnabled);
      setRemember(cfg.rememberEnabled);
    });
    return () => {
      active = false;
    };
  }, []);

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    setError(false);
    login(
      { username, password, remember },
      {
        onSuccess: (data) => {
          if (data && data.success === false) {
            setError(true);
          }
        },
        onError: () => setError(true),
      },
    );
  };

  const onLocaleChange = (code: string) => {
    // The dependency-free i18nProvider swaps the active bundle on a variable; reload re-resolves it
    // from the persisted override so the whole page re-renders in the chosen language.
    markeerTaalGekozen(); // na het inloggen gaat deze keuze mee met het account
    void setLocale(code);
    globalThis.location?.reload();
  };

  return (
    <div className="login-main">
      <form className="login-card" onSubmit={onSubmit}>
        <div className="login-header">
          <img className="login-title-icon" src="/login/login-lock.png" alt="" aria-hidden="true" />
          <h1 className="login-title">{t("login.headerTitle", undefined, "Docasoft")}</h1>
        </div>

        {error && (
          <p className="login-error" role="alert">
            {t("login.badCredentials", undefined, "Controleer je gebruikersnaam en wachtwoord.")}
          </p>
        )}

        <Field
          label={t("login.username", undefined, "Gebruikersnaam")}
          icon={<UserIcon />}
          autoComplete="username"
          autoFocus
          value={username}
          onChange={(e) => setUsername(e.target.value)}
        />
        <PasswordField
          label={t("login.password", undefined, "Wachtwoord")}
          icon={<LockIcon />}
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          revealLabel={t("login.showPassword", undefined, "Wachtwoord tonen")}
          hideLabel={t("login.hidePassword", undefined, "Wachtwoord verbergen")}
        />

        {rememberOffered && (
          <ToggleField
            label={t("login.rememberMe", undefined, "Onthoud mij")}
            checked={remember}
            onChange={(e) => setRemember(e.target.checked)}
          />
        )}

        <label className="login-locale">
          <span className="login-locale__flag" aria-hidden="true">
            {LOCALES.find((l) => l.code === locale)?.flag ?? "🌐"}
          </span>
          <select
            className="login-locale__select"
            value={locale}
            aria-label={t("login.locale", undefined, "Taal")}
            onChange={(e) => onLocaleChange(e.target.value)}
          >
            {LOCALES.map((l) => (
              // The flag is rendered once by the .login-locale__flag badge prefix; keep the option
              // text label-only so the closed select doesn't show a second flag next to the badge.
              <option key={l.code} value={l.code}>
                {l.label}
              </option>
            ))}
          </select>
          {/* Custom dropdown chevron (native arrow hidden in CSS) — sized/aligned to match the
              password field's reveal eye so the login controls feel coherent. */}
          <span className="login-locale__chevron" aria-hidden="true">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.8}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </span>
        </label>

        <Button type="submit" variant="primary" disabled={isLoading} className="login-submit">
          {t("login.submit", undefined, "Inloggen")}
        </Button>

        {PROVISIONING_LOGIN_ENABLED && (
          <Button type="button" variant="secondary" className="login-provisioning">
            {t("login.provisioningLogin", undefined, "Inloggen met HelloID")}
          </Button>
        )}
      </form>
    </div>
  );
}
